import { useMutation, UseMutationResult } from '@tanstack/react-query'

import { getRudderstackAnonymousIdWithTimeout } from '@/hooks/useRudderstackAnalytics'
import { getV3ApiUrl } from '@/utils/urlHelpers'

export interface UserSubscriptionPayload {
  email: string
  anonymous_id?: string
  // Google reCAPTCHA v3 token. The backend's verify_recaptcha looks for the
  // 'g-recaptcha-response' field by default, so we send it under that key.
  'g-recaptcha-response'?: string
}

export interface UserSubscriptionResponse {
  success?: boolean
  status?: string
  message: string
}

const GENERIC_ERROR = 'Something went wrong. Please try again.'

const postUserSubscriptionApi = async (
  payload: UserSubscriptionPayload,
): Promise<UserSubscriptionResponse> => {
  const url = getV3ApiUrl('/user_subscriptions')

  // Newsletter forms read the SDK id synchronously and get undefined before it
  // loads, which left the backend minting its own id for generate_lead. Fall
  // back to a bounded wait; JSON.stringify drops the key when still undefined.
  const anonymousId = payload.anonymous_id ?? (await getRudderstackAnonymousIdWithTimeout(1000))

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, anonymous_id: anonymousId }),
  })

  const data = await response.json()

  // The backend returns HTTP 200 even for failures, signalling the outcome via
  // the `success` field, so we must check it in addition to the HTTP status.
  if (!response.ok || data?.success === false) {
    throw new Error(data?.message || GENERIC_ERROR)
  }

  return data as UserSubscriptionResponse
}

const useUserSubscriptionsApi = (): UseMutationResult<
  UserSubscriptionResponse,
  Error,
  UserSubscriptionPayload,
  unknown
> => {
  return useMutation<UserSubscriptionResponse, Error, UserSubscriptionPayload>({
    mutationFn: postUserSubscriptionApi,
    retry: false,
  })
}

export default useUserSubscriptionsApi
